import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert, Button, Card } from '@dreamtree-org/twreact-ui';
import { Smartphone } from 'lucide-react';
import { useMutation } from '@/lib/useQuery';
import { useAppApi } from '@/lib/api';
import { errorText } from '@/lib/format';
import PhoneOtpForm, { useSignInMethod } from '../components/PhoneOtpForm';
import { useStorefront } from '../StorefrontContext';

// Demo shopper used by the "Continue as demo customer" shortcut (demo environments only).
const DEMO = {
  phone: '9876543210',
  profile: { name: 'Rahul Sharma', email: 'rahul.sharma@example.in' },
  address: { label: 'Home', name: 'Rahul Sharma', phone: '9876543210', line1: '123, 4th Main, HAL 2nd Stage', line2: 'Indiranagar', city: 'Bengaluru', state: 'Karnataka', pin: '560038', is_default: true },
};

/**
 * WEB-06 phone OTP sign-in. Guests browse freely; this appears at checkout and
 * for account pages (C-05). Sends are rate-limited (3 per number). Consent for
 * marketing messages is a separate, unticked, optional control.
 * Real OTP: the server issues a one-time code (5 min, 5 attempts). In demo
 * environments (OTP_DEV_ECHO) the code comes back as `dev_code` and is shown here;
 * in production it only arrives by SMS.
 * Firebase (D30): when the store's sign-in method is `firebase`, Firebase sends
 * and checks the SMS in the browser and the server verifies its ID token.
 * The form itself is components/PhoneOtpForm (shared with the checkout step, MERCH-56).
 */
// Connecting an AI assistant (D45): sign-in returns to the store's OAuth server, outside the SPA.
const SERVER_RETURN = /^\/oauth\/authorize\?/;

export default function Login() {
  const sf = useStorefront();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = params.get('next') || '/account';
  const serverReturn = SERVER_RETURN.test(next);
  const done = () => (serverReturn ? window.location.replace(next) : navigate(next, { replace: true }));
  const { fetchApi } = useAppApi();
  const send = useMutation('sfOtpSend');
  const verify = useMutation('sfOtpVerify');
  const method = useSignInMethod();
  const [err, setErr] = useState('');

  useEffect(() => {
    if (serverReturn && sf.sessionReady && sf.session) window.location.replace(next);
  }, [serverReturn, sf.sessionReady, sf.session, next]);

  if (sf.sessionReady && sf.session) return serverReturn ? null : <Navigate to={next} replace />;

  const verified = (session) => {
    sf.setSession(session);
    done();
  };

  const demo = async () => {
    setErr('');
    try {
      const s = await send.run({ phone: DEMO.phone });
      if (!s.data.dev_code) return setErr('The demo shortcut only works in demo environments. Sign in with your own number.');
      let r = await verify.run({ phone: DEMO.phone, code: s.data.dev_code, request_id: s.data.request_id });
      // First visit: give the demo shopper a name and an address (real records, via the real API).
      if (!r.data.name) r = await fetchApi('sfProfileSave', DEMO.profile, false);
      const acct = await fetchApi('sfAccount', {}, false);
      if (!acct.data.addresses.length) await fetchApi('sfAddressSave', DEMO.address, false);
      sf.setSession(r.data);
      done();
    } catch (ex) {
      setErr(errorText(ex));
    }
  };

  return (
    <div className="mx-auto max-w-md space-y-4 py-2 sm:py-6">
      <Card className="space-y-5 p-5 sm:p-6">
        <div className="flex items-center gap-3">
          <span className="flex h-11 w-11 items-center justify-center rounded-full bg-primary-50 text-primary-600"><Smartphone className="h-5 w-5" aria-hidden="true" /></span>
          <div>
            <h1 className="text-xl font-bold">Sign in to {sf.brand.name}</h1>
            <p className="text-sm opacity-70">{serverReturn ? 'Sign in to connect your AI assistant to this store.' : next.startsWith('/checkout') ? 'Sign in to place your order.' : 'Track orders, repairs and warranty.'}</p>
          </div>
        </div>

        <PhoneOtpForm method={method} onVerified={verified} />
        {err ? <Alert variant="error">{err}</Alert> : null}

        {method && method.demo ? <div className="border-t border-[var(--app-border)] pt-4">
          <Button variant="outline" fullWidth onClick={demo} loading={send.loading || verify.loading}>Continue as demo customer</Button>
          <p className="mt-2 text-center text-xs opacity-60">Demo environments only: signs in as Rahul Sharma (+91 98765 43210).</p>
        </div> : null}
      </Card>
    </div>
  );
}
